import {
  persistPreferredLanguage,
} from "../utils/avaaioLanguage";
import {
  usePreferences,
} from "../context/PreferencesContext";
import {
  getAvaaioAuthCopy,
  localizeAuthResponse,
} from "../utils/avaaioAuthCopy";
// File: /home/ubuntu/avaai_dashboard/frontend/src/pages/Login.tsx

import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import Cookies from "js-cookie";
import "../styles/LoginForm.css";
import GlobalFooter from "../components/GlobalFooter";
import AvaaioStarRating, {
  submitPendingAvaaioRating,
} from "../components/AvaaioStarRating";

import { writeLastVerifiedOfflineUser } from "../utils/authCache";
const API_BASE =
  import.meta.env.VITE_API_BASE ||
  "";

const SESSION_COOKIE_NAME = "ava_session_token";
const USER_COOKIE_NAME = "ava_user";
const LOGIN_TIMEOUT_MS = 12000;

const isAdminLike = (payload: any): boolean => {
  if (!payload || typeof payload !== "object") return false;

  const role = String(
    payload.role ??
    payload.user?.role ??
    payload.profile?.role ??
    ""
  ).toLowerCase();

  return (
    payload.is_admin === true ||
    payload.user?.is_admin === true ||
    payload.profile?.is_admin === true ||
    payload.admin === true ||
    payload.user?.admin === true ||
    role === "admin" ||
    role === "superadmin" ||
    role === "owner"
  );
};


const Login: React.FC = () => {
  const { preferences } = usePreferences();
  const copy = getAvaaioAuthCopy(
    preferences.language
  );

  const continueWithApple = async () => {
    window.location.href =
      `/api/v1/auth/apple/start?ui_language=${
        encodeURIComponent(copy.language)
      }`;
  };


  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState<"idle" | "typing" | "success">("idle");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showReactivate, setShowReactivate] = useState(false);
  const eyeRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    setStatus(email.length > 0 || password.length > 0 ? "typing" : "idle");
  }, [email, password]);

  // AVA_MODEL_LOGIN_PRELOAD
  // Warm Ava's existing full model while the login form is visible.
  useEffect(() => {
    const warmAvaModel = () => {
      fetch("/models/AVA_REVISED.glb", {
        cache: "force-cache",
      }).catch(() => {});
    };

    const requestIdle = (window as any).requestIdleCallback;

    if (typeof requestIdle === "function") {
      const id = requestIdle(warmAvaModel, { timeout: 1200 });

      return () => {
        (window as any).cancelIdleCallback?.(id);
      };
    }

    const timer = window.setTimeout(warmAvaModel, 250);
    return () => window.clearTimeout(timer);
  }, []);

  const runLoginRequest = async (
    loginUrl: string,
    payload: {
      email: string;
      password: string;
      ui_language: string;
    }
  ) => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), LOGIN_TIMEOUT_MS);

    try {
      const response = await fetch(loginUrl, {         method: "POST",
        credentials: "include",
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      const rawText = await response.text();
      let data: any = {};

      try {
        data = rawText ? JSON.parse(rawText) : {};
      } catch {
        throw new Error("INVALID_JSON_RESPONSE");
      }

      return { response, data };
    } finally {
      window.clearTimeout(timeout);
    }
  };



  const reactivateAccount = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/auth/reactivate-account`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          ui_language: copy.language,
        }),
      });

      const data = await res.json();

      if (data?.ok) {
        try {
          [
            "auth_user",
            "ava_user",
            "user",
            "profile",
            "ava_profile",
            "user_profile",
            "ava_chat_plan",
            "ava_user_role",
            "access_token",
            "authToken",
            "jwt",
            "token",
            "ava_token",
            "ava_session_token"
          ].forEach((k) => {
            localStorage.removeItem(k);
            sessionStorage.removeItem(k);
          });

          window.dispatchEvent(new Event("ava-force-logout"));
          // Account is not reactivated yet; email confirmation required.
        } catch {}

        alert(
          localizeAuthResponse(
            data?.message,
            copy.login.reactivationSent,
            copy
          )
        );
        setShowReactivate(false);
        // Stay on login screen. Account is not restored until the email link is clicked.
      } else {
        alert(copy.login.reactivationFailed);
      }
    } catch (err) {
      console.error(err);
      alert(copy.login.reactivationError);
    }
  };

const persistAuth = (token: string, user: any) => {
    Cookies.set(SESSION_COOKIE_NAME, token, {
      secure: true,
      sameSite: "Lax",
      expires: 7,
    });

    localStorage.setItem(SESSION_COOKIE_NAME, token);
    localStorage.setItem("token", token);
    localStorage.setItem("ava_token", token);

    sessionStorage.setItem(SESSION_COOKIE_NAME, token);
    sessionStorage.setItem("token", token);
    sessionStorage.setItem("ava_token", token);

    if (user) {
      const serializedUser = JSON.stringify(user);

      Cookies.set(USER_COOKIE_NAME, serializedUser, {
        secure: true,
        sameSite: "Lax",
        expires: 7,
      });

      localStorage.setItem(USER_COOKIE_NAME, serializedUser);
      sessionStorage.setItem(USER_COOKIE_NAME, serializedUser);

      persistPreferredLanguage(
        user?.preferred_language || "en"
      );

      try {
        const role = String(user?.role || "").toLowerCase();
        const sub = String(user?.subscription_status || "").toLowerCase();
        const isAdmin = ["admin", "owner", "superadmin"].includes(role);
        const isPro = sub === "active" || user?.vip_lifetime === true;

        // === FORCE BACKEND PLAN TRUTH ===
        // Never trust stale localStorage plan cache.
        const plan =
          isAdmin ? "admin" :
          isPro ? "pro" :
          "free";

        // wipe stale values first
        localStorage.removeItem("ava_chat_plan");
        sessionStorage.removeItem("ava_chat_plan");

        // sync from backend truth only
        localStorage.setItem("ava_chat_plan", plan);
        sessionStorage.setItem("ava_chat_plan", plan);

        localStorage.setItem(
          "subscription_status",
          String(user?.subscription_status || "inactive")
        );

        localStorage.setItem(
          "ava_user_role",
          role || "user"
        );
        sessionStorage.setItem("ava_user_role", role || "user");
      } catch {}
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (loading) return;

    if (!email.trim() || !password.trim()) {
      setError(copy.login.enterCredentials);
      setStatus("idle");
      return;
    }

    setError("");
    setLoading(true);


      // =====================================================
      // HARD AUTH RESET BEFORE NEW LOGIN
      // Prevent stale admin/pro auth leakage across accounts
      // =====================================================
      try {
        const authKeys = [
          "ava_user",
          "auth_user",
          "user",
          "profile",
          "ava_profile",
          "user_profile",
          "ava_chat_plan",
          "ava_user_role",
          "ava_session_token",
          "ava_token",
          "token",
          "access_token",
          "authToken",
          "jwt"
        ];

        for (const k of authKeys) {
          localStorage.removeItem(k);
          sessionStorage.removeItem(k);
        }
      } catch {}

      const payload = {
      email: email.trim().toLowerCase(),
      password,
      ui_language: copy.language,
    };

    try {
      const { response, data } = await runLoginRequest(
        `${API_BASE}/api/v1/auth/login`,
        payload
      );

      if (data?.next_step === "reactivate_account") {
        setShowReactivate(true);
        setError("");
        setStatus("idle");
        return;
      }

      if (!response.ok) {
        setError(
          localizeAuthResponse(
            data?.detail || data?.message,
            copy.login.loginFailed,
            copy
          )
        );
        setStatus("idle");
        return;
      }

      const loginNextStep = String(
        data?.next_step || ""
      )
        .trim()
        .toLowerCase();

      const loginUser =
        data?.user ||
        data?.profile ||
        data?.me ||
        null;

      const verificationValue =
        loginUser?.email_verified ??
        loginUser?.is_verified;

      const loginEmailVerified =
        verificationValue === true ||
        verificationValue === 1 ||
        verificationValue === "1";

      if (
        loginNextStep === "verify_email" ||
        (loginUser && !loginEmailVerified)
      ) {
        try {
          const verificationAuthKeys = [
            "ava_session_token",
            "ava_token",
            "token",
            "access_token",
            "authToken",
            "jwt",
            "auth_user",
          ];

          for (const key of verificationAuthKeys) {
            localStorage.removeItem(key);
            sessionStorage.removeItem(key);
          }
        } catch {}

        setError(
          data?.message ||
            "Please verify your email before continuing."
        );
        setStatus("idle");
        return;
      }

      if (data?.ok === false) {
        setError(
          localizeAuthResponse(
            data?.message || data?.detail,
            copy.login.loginFailed,
            copy
          )
        );
        setStatus("idle");
        return;
      }

      const token =
        data?.access_token ||
        data?.token ||
        data?.jwt ||
        data?.session_token ||
        "";

      const user =
        data?.user ||
        data?.profile ||
        data?.me ||
        null;

      try {
        sessionStorage.removeItem("ava_logged_out");
      } catch {}

      if (token) {
        try {
          persistAuth(token, user);
        } catch {}

        try {
          localStorage.setItem("access_token", token);
          localStorage.setItem("authToken", token);
          localStorage.setItem("jwt", token);

          sessionStorage.setItem("access_token", token);
          sessionStorage.setItem("authToken", token);
          sessionStorage.setItem("jwt", token);
        } catch {}
      }

      if (user) {
        try {
          const serializedUser = JSON.stringify(user);
          localStorage.setItem("auth_user", serializedUser);
          sessionStorage.setItem("auth_user", serializedUser);

          // Save only a sanitized standard-user identity for
          // approved Local Mode access while offline.
          // No password, token or administrator authority is copied.
          writeLastVerifiedOfflineUser(user);
        } catch {}
      }

      // Submit a star choice made before login. Failure never
      // blocks authentication and keeps the pending rating saved.
      try {
        await submitPendingAvaaioRating(
          API_BASE,
          token
        );
      } catch {}

      setStatus("success");

        // === FORCE UI STATE SYNC (CRITICAL FIX) ===
        try {
          window.dispatchEvent(new Event("ava-plan-updated"));
          window.dispatchEvent(new Event("ava-force-refresh"));
        } catch {}


      // =============================
      // AVA LOGIN FLOW ROUTING
      // =============================

        // NEVER trust stale cached admin state
        // Only trust freshly returned user object
        const normalizedRole = String(
          user?.role ||
          data?.role ||
          ""
        ).toLowerCase();

        const isAdmin =
          normalizedRole === "admin" ||
          normalizedRole === "owner" ||
          normalizedRole === "superadmin";


      const profileIncomplete =
        !user ||
        user.profile_complete === false ||
        (!user.name && !user.username);

      if (isAdmin) {
        window.location.replace("/admin");
      } else if (profileIncomplete) {
        window.location.replace("/setup-profile");
      } else {
        window.location.replace("/dashboard");
      }
    } catch (err: any) {
      if (err?.name === "AbortError") {
        setError(copy.login.timedOut);
      } else if (err?.message === "INVALID_JSON_RESPONSE") {
        setError(copy.login.invalidResponse);
      } else {
        setError(copy.login.networkError);
      }
      setStatus("idle");
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    // __AVAAIO_LOGIN_EYE_LIVING_MOTION__
    const eye = eyeRef.current;
    if (!eye) return;

    eye.style.pointerEvents = "none";
    eye.style.willChange =
      "transform, opacity, filter";

    const reducedMotion =
      window.matchMedia?.(
        "(prefers-reduced-motion: reduce)"
      ).matches ?? false;

    let targetX = 0;
    let targetY = 0;
    let currentX = 0;
    let currentY = 0;
    let lastInteractionAt =
      performance.now();

    let animationFrame = 0;

    const clamp = (
      value: number,
      minimum: number,
      maximum: number
    ) => {
      return Math.min(
        maximum,
        Math.max(minimum, value)
      );
    };

    const pointEyeToward = (
      x: number,
      y: number
    ) => {
      const rect =
        eye.getBoundingClientRect();

      const centerX =
        rect.left + rect.width / 2;

      const centerY =
        rect.top + rect.height / 2;

      /*
       * Stronger than the previous /25 movement, but bounded
       * so the eye never leaves its atmospheric background area.
       */
      targetX = clamp(
        (x - centerX) / 14,
        -46,
        46
      );

      targetY = clamp(
        (y - centerY) / 17,
        -31,
        31
      );

      lastInteractionAt =
        performance.now();
    };

    const handleMouseMove = (
      event: MouseEvent
    ) => {
      pointEyeToward(
        event.clientX,
        event.clientY
      );
    };

    const handleTouchMove = (
      event: TouchEvent
    ) => {
      const touch = event.touches?.[0];

      if (!touch) return;

      pointEyeToward(
        touch.clientX,
        touch.clientY
      );
    };

    const animateEye = (
      timestamp: number
    ) => {
      const inactiveFor =
        timestamp - lastInteractionAt;

      /*
       * After the pointer becomes still, the eye slowly explores
       * the background in a layered orbit instead of freezing.
       */
      if (
        inactiveFor > 1150 &&
        !reducedMotion
      ) {
        const seconds =
          timestamp * 0.001;

        targetX =
          Math.sin(seconds * 0.34) * 25 +
          Math.sin(seconds * 0.13 + 1.4) * 9;

        targetY =
          Math.cos(seconds * 0.27 + 0.6) * 15 +
          Math.sin(seconds * 0.11) * 6;
      }

      if (reducedMotion) {
        targetX = clamp(
          targetX,
          -10,
          10
        );

        targetY = clamp(
          targetY,
          -7,
          7
        );
      }

      const followRate =
        inactiveFor > 1150
          ? 0.022
          : 0.075;

      currentX +=
        (targetX - currentX) *
        followRate;

      currentY +=
        (targetY - currentY) *
        followRate;

      const pulse =
        reducedMotion
          ? 1
          : 1 +
            Math.sin(
              timestamp * 0.00072
            ) * 0.022;

      const glowShift =
        reducedMotion
          ? 0
          : Math.sin(
              timestamp * 0.00055
            ) * 0.08;

      eye.style.transform =
        `translate(-50%, -50%) ` +
        `translate3d(${currentX}px, ${currentY}px, 0) ` +
        `scale(${pulse})`;

      eye.style.opacity =
        String(0.9 + glowShift);

      animationFrame =
        window.requestAnimationFrame(
          animateEye
        );
    };

    window.addEventListener(
      "mousemove",
      handleMouseMove,
      {
        passive: true,
      }
    );

    window.addEventListener(
      "touchmove",
      handleTouchMove,
      {
        passive: true,
      }
    );

    animationFrame =
      window.requestAnimationFrame(
        animateEye
      );

    return () => {
      window.removeEventListener(
        "mousemove",
        handleMouseMove
      );

      window.removeEventListener(
        "touchmove",
        handleTouchMove
      );

      window.cancelAnimationFrame(
        animationFrame
      );

      eye.style.willChange = "";
      eye.style.opacity = "";
    };
  }, []);

  return (
    <div className="dark-bg">
      <div
        ref={eyeRef}
        className={`ava-eye ${
          status === "idle"
            ? "color-red"
            : status === "typing"
            ? "color-mint"
            : "color-green"
        }`}
      />

      <div className="mint-login-container">
        <div className="avaaio-login-stage">
          <div className="mint-login-card" style={{ position: "relative", zIndex: 10 }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              marginBottom: 24,
              width: "100%",
            }}
          >
            <div
              style={{
                color: "#d9feff",
                fontSize: 20,
                letterSpacing: 6,
                fontWeight: 800,
                marginBottom: 8,
                textTransform: "uppercase",
                opacity: 0.96,
              }}
            >
              {copy.login.welcomeTo}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "minmax(0, 1fr) auto minmax(0, 1fr)",
                alignItems: "center",
                width: "100%",
              }}

              className="avaaio-login-title-row"
            >
              <h1
                className="mint-title avaaio-login-title"
                style={{
                  gridColumn: 2,
                  justifySelf: "center",
                  margin: 0,
                  lineHeight: 0.92,
                  letterSpacing: 2,
                  textAlign: "center",
                  whiteSpace: "nowrap",
                }}
              >
                AVAAIO™
              </h1>

              <span
                style={{
                  position: "static",
                  gridColumn: 3,
                  justifySelf: "start",
                  minWidth: 58,
                  textAlign: "center",
                  padding: "6px 14px",
                  borderRadius: 999,
                  border: "1px solid rgba(0,255,255,0.38)",
                  background: "rgba(80,120,255,0.14)",
                  color: "#8ffcff",
                  fontSize: 11,
                  fontWeight: 900,
                  letterSpacing: 2.2,
                  whiteSpace: "nowrap",
                  boxShadow: "0 0 10px rgba(0,255,255,0.16)",
                }}

                className="avaaio-login-beta-badge"
              >
                {copy.login.beta}
              </span>
            </div>

            <section
              className="avaaio-login-brand-intro"
              aria-label={copy.login.meetAva}
            >
              <p className="avaaio-login-meet-ava">
                {copy.login.meetAva}
              </p>

              <p className="avaaio-login-generation">
                {copy.login.generation}
              </p>
            </section>
          </div>


{/* ===== FORCED REACTIVATE MODE ===== */}
{showReactivate && (
  <div style={{
    marginBottom: "20px",
    padding: "18px",
    borderRadius: "16px",
    background: "rgba(255,0,0,0.08)",
    border: "1px solid rgba(255,0,0,0.4)",
    textAlign: "center"
  }}>
    <div style={{ fontSize: "16px", fontWeight: 800, marginBottom: 10 }}>
      {copy.login.accountDeleted}
    </div>

    <div style={{ fontSize: "13px", marginBottom: 14 }}>
      {copy.login.deletedCannotLogin}
    </div>

    <button
      type="button"
      onClick={reactivateAccount}
      style={{
        padding: "12px 18px",
        borderRadius: "12px",
        border: "1px solid rgba(0,255,255,0.6)",
        background: "rgba(110,80,255,0.18)",
        color: "#9ffcff",
        fontWeight: 800,
        cursor: "pointer"
      }}
    >
      {copy.login.reactivate}
    </button>
  </div>
)}

<form
            style={{ display: showReactivate ? "none" : undefined }}
            onSubmit={(e) => {
              void handleSubmit(e);
            }}
          >
            <div className="input-wrapper">
              <input
                type="email"
                inputMode="email"
                placeholder={copy.login.email}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                required
              />
              <div className="neon-border" />
            </div>

            <div className="input-wrapper">
              <input
                type="password"
                placeholder={copy.login.password}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                required
              />
              <div className="neon-border" />
            </div>

            <button type="submit" disabled={loading || showReactivate}>
              {loading ? copy.login.signingIn : copy.login.signIn}
            </button>



        <button
          type="button"
          onClick={continueWithApple}
          style={{
            width: "100%",
            minHeight: "48px",
            marginTop: "12px",
            borderRadius: "14px",
            border: "1px solid rgba(255,255,255,0.18)",
            background: "#000000",
            color: "#ffffff",
            fontWeight: 700,
            cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
              textAlign: "center",
              boxSizing: "border-box"
          }}
        >
          <img
            src="/apple-logo.svg"
            alt=""
            aria-hidden="true"
            style={{
              width: 20,
              height: 20,
              display: "block",
              objectFit: "contain",
              flex: "0 0 auto",
            }}
          />

          {copy.login.continueApple}
        </button>

{showReactivate && (
  <button
    type="button"
    onClick={reactivateAccount}
    style={{
      marginTop: "12px",
      padding: "10px 14px",
      borderRadius: "10px",
      border: "1px solid rgba(0,255,255,0.5)",
      background: "rgba(90,120,255,0.10)",
      color: "#9ffcff",
      fontWeight: 700,
      cursor: "pointer"
    }}
  >
    {copy.login.reactivate}
  </button>
)}


{/* ============================= */}
{/* REACTIVATE ACCOUNT BLOCK START */}
{showReactivate && (
  <div style={{
    marginTop: "18px",
    padding: "14px",
    borderRadius: "14px",
    border: "1px solid rgba(255,180,0,0.5)",
    background: "rgba(255,180,0,0.08)",
    color: "#ffd27a",
    textAlign: "center"
  }}>
    <div style={{ fontWeight: 700, marginBottom: 8 }}>
      {copy.login.deleted}
    </div>

    <div style={{ fontSize: "13px", marginBottom: 12 }}>
      {copy.login.restoreInstantly}
    </div>

    <button
      type="button"
      onClick={reactivateAccount}
      style={{
        padding: "10px 16px",
        borderRadius: "10px",
        border: "1px solid rgba(0,255,255,0.6)",
        background: "rgba(140,90,255,0.16)",
        color: "#9ffcff",
        fontWeight: 700,
        cursor: "pointer"
      }}
    >
      {copy.login.reactivate}
    </button>
  </div>
)}
{/* REACTIVATE ACCOUNT BLOCK END */}

</form>

          {error && <div className="error">{error}</div>}

          <div className="links">
            <Link to="/register" className="neon-link">{copy.login.register}</Link>
            <span className="links-separator">|</span>
            <Link to="/forgot-password" className="neon-link">{copy.login.forgotPassword}</Link>
          </div>



          <div className="reflection" />
          </div>

          <AvaaioStarRating />
        </div>

        <GlobalFooter compact />
      </div>
    </div>
  );
};

export default Login;

